import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useLanguage } from '../contexts/LanguageContext';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { CheckCircle, Clock, Smartphone, XCircle, ZoomIn } from 'lucide-react';

export type SimaSignResult = 'waiting' | 'signed' | 'rejected' | 'expired';

interface SimaSignModalProps {
  open: boolean;
  onClose: () => void;
  qrCodeValue: string;
  result?: SimaSignResult | null;
}

const QR_ICON_SRC = `${process.env.PUBLIC_URL}/qr-icon.svg`;

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function iconUriFromPayload(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as {
    SignableContainer?: { ClientInfo?: { IconURI?: unknown } };
    signableContainer?: { clientInfo?: { iconURI?: unknown; IconURI?: unknown } };
  };
  const client =
    record.SignableContainer?.ClientInfo ?? record.signableContainer?.clientInfo;
  const uri = client?.IconURI ?? (client && 'iconURI' in client ? client.iconURI : undefined);
  return typeof uri === 'string' && isHttpUrl(uri) ? uri : null;
}

function decodeBase64Json(encoded: string): unknown | null {
  try {
    const normalized = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const padding = normalized.length % 4 === 0 ? '' : '='.repeat(4 - (normalized.length % 4));
    return JSON.parse(atob(normalized + padding));
  } catch {
    return null;
  }
}

function readClientIconUri(qrText: string): string | null {
  const trimmed = qrText.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('{')) {
    try {
      return iconUriFromPayload(JSON.parse(trimmed));
    } catch {
      return null;
    }
  }

  let dataParam: string | null = null;
  try {
    const outer = new URL(trimmed.replace(/^sima:\/\//, 'https://'));
    dataParam = outer.searchParams.get('tsquery') ?? outer.searchParams.get('data');
  } catch {
    return null;
  }
  if (!dataParam) return null;

  try {
    const inner = new URL(dataParam);
    const tsquery = inner.searchParams.get('tsquery');
    if (tsquery) return iconUriFromPayload(decodeBase64Json(tsquery));
  } catch {
    // data itself may be the encoded payload
  }

  return iconUriFromPayload(decodeBase64Json(dataParam));
}

function getZoomedQrSize(): number {
  if (typeof window === 'undefined') return 320;
  return Math.floor(Math.min(window.innerWidth, window.innerHeight) * 0.85 - 32);
}

const SimaSignModal: React.FC<SimaSignModalProps> = ({
  open,
  onClose,
  qrCodeValue,
  result = 'waiting',
}) => {
  const { t } = useLanguage();
  const isDesktop = useIsDesktop();
  const qrContainerRef = useRef<HTMLDivElement>(null);
  const [qrSize, setQrSize] = useState(420);
  const [qrZoomed, setQrZoomed] = useState(false);
  const [zoomedQrSize, setZoomedQrSize] = useState(getZoomedQrSize);
  const showQr = result === 'waiting' || result == null;
  const remoteIconUri = useMemo(() => readClientIconUri(qrCodeValue), [qrCodeValue]);
  const [qrIconSrc, setQrIconSrc] = useState(QR_ICON_SRC);

  useEffect(() => {
    if (!remoteIconUri) {
      setQrIconSrc(QR_ICON_SRC);
      return;
    }

    let cancelled = false;
    const image = new Image();
    image.onload = () => {
      if (!cancelled) setQrIconSrc(remoteIconUri);
    };
    image.onerror = () => {
      if (!cancelled) setQrIconSrc(QR_ICON_SRC);
    };
    image.src = remoteIconUri;

    return () => {
      cancelled = true;
    };
  }, [remoteIconUri]);

  useEffect(() => {
    const node = qrContainerRef.current;
    if (!node) return;

    const updateSize = () => {
      const width = node.getBoundingClientRect().width;
      setQrSize(Math.min(420, Math.max(240, Math.floor(width - 16))));
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(node);
    return () => observer.disconnect();
  }, [open, showQr]);

  useEffect(() => {
    if (!open || !showQr) {
      setQrZoomed(false);
    }
  }, [open, showQr]);

  useEffect(() => {
    if (!qrZoomed) return;

    const updateZoomSize = () => setZoomedQrSize(getZoomedQrSize());
    updateZoomSize();
    window.addEventListener('resize', updateZoomSize);
    return () => window.removeEventListener('resize', updateZoomSize);
  }, [qrZoomed]);

  useEffect(() => {
    if (!qrZoomed) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setQrZoomed(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [qrZoomed]);

  const handleOpenSignerApp = () => {
    if (!qrCodeValue) return;
    window.location.href = qrCodeValue;
  };

  const qrCode = (size: number) => {
    const iconSize = Math.max(36, Math.round(size * 0.22));

    return (
      <QRCodeSVG
        value={qrCodeValue}
        size={size}
        level="H"
        includeMargin={true}
        imageSettings={{
          src: qrIconSrc,
          height: iconSize,
          width: iconSize,
          excavate: true,
        }}
        className="mx-auto h-auto w-full max-w-full"
      />
    );
  };

  const resultContent = () => {
    if (result === 'signed') {
      return (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <CheckCircle className="h-12 w-12 text-green-600" aria-hidden />
          <p className="text-sm text-foreground">{t('simaSignThanks')}</p>
        </div>
      );
    }
    if (result === 'rejected') {
      return (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <XCircle className="h-12 w-12 text-destructive" aria-hidden />
          <p className="text-sm text-foreground">{t('signingPartyStatusRejected')}</p>
        </div>
      );
    }
    if (result === 'expired') {
      return (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <Clock className="h-12 w-12 text-muted-foreground" aria-hidden />
          <p className="text-sm text-foreground">{t('signingPartyStatusExpired')}</p>
        </div>
      );
    }
    return null;
  };

  const title =
    result === 'signed'
      ? t('signed')
      : result === 'rejected'
        ? t('signingPartyStatusRejected')
        : result === 'expired'
          ? t('signingPartyStatusExpired')
          : t('simaSignModalTitle');

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-[468px]">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          {showQr ? (
            <div className="flex flex-col items-center gap-3 py-2">
              {!isDesktop && (
                <Button className="w-full" onClick={handleOpenSignerApp}>
                  <Smartphone className="h-4 w-4" />
                  {t('simaOpenSignerApp')}
                </Button>
              )}
              <div ref={qrContainerRef} className="w-full max-w-[420px]">
                <button
                  type="button"
                  onClick={() => setQrZoomed(true)}
                  className="group w-full rounded-lg border p-2 transition hover:border-primary hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  aria-label={t('simaEnlargeQr')}
                >
                  {qrCode(qrSize)}
                  <span className="mt-2 flex items-center justify-center gap-1 text-xs text-muted-foreground group-hover:text-foreground">
                    <ZoomIn className="h-3.5 w-3.5" />
                    {t('simaTapQrToZoom')}
                  </span>
                </button>
              </div>
              <p className="text-center text-sm text-muted-foreground">
                {isDesktop ? t('simaScanQrDesktop') : t('simaScanQrMobile')}
              </p>
              <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <span
                  className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"
                  aria-hidden
                />
                {t('simaSignWaiting')}
              </p>
              {isDesktop && (
                <Button className="w-full" variant="outline" onClick={handleOpenSignerApp}>
                  <Smartphone className="h-4 w-4" />
                  {t('simaOpenSignerApp')}
                </Button>
              )}
            </div>
          ) : (
            resultContent()
          )}
        </DialogContent>
      </Dialog>

      {qrZoomed &&
        showQr &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/90 p-4"
            onClick={() => setQrZoomed(false)}
            role="dialog"
            aria-modal="true"
            aria-label={t('simaEnlargedQr')}
          >
            <div
              className="rounded-none bg-white p-4 shadow-lg"
              onClick={(event) => event.stopPropagation()}
            >
              {qrCode(zoomedQrSize)}
            </div>
            <p className="mt-4 text-center text-sm text-white/80">{t('simaTapToCloseQr')}</p>
          </div>,
          document.body,
        )}
    </>
  );
};

export default SimaSignModal;
